import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, TrendingUp, Users, CheckCircle, Contact, FileText, ClipboardCheck, ChevronRight } from 'lucide-react';
import { StatCard } from '../ui';
import { formatCurrency } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSocket } from '../../hooks/useSocket';
import { approvalService } from '../../services/api';

export default function DashboardView({ stats = {}, currentUser = null, onShowRevenue }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { socket } = useSocket();

  const isApproverUser = ['CEO', 'GM_Operations', 'BM_Operations'].includes(currentUser?.role);
  const [approvalCounts, setApprovalCounts] = useState({
    totalPending: 0,
    pendingProfiles: 0,
    pendingPrequalifications: 0,
    pendingTenders: 0,
  });
  const [approvalLoading, setApprovalLoading] = useState(false);

  const fetchApprovalCounts = useCallback(async () => {
    if (!isApproverUser) return;
    try {
      setApprovalLoading(true);
      const res = await approvalService.getCounts();
      if (res?.status && res.data) {
        setApprovalCounts(res.data);
      }
    } catch (err) {
      console.error('Failed to load approval counts in DashboardView:', err);
    } finally {
      setApprovalLoading(false);
    }
  }, [isApproverUser]);

  useEffect(() => {
    fetchApprovalCounts();
  }, [fetchApprovalCounts]);

  useEffect(() => {
    if (!socket || !isApproverUser) return;
    const handleUpdate = () => {
      fetchApprovalCounts();
    };
    socket.on('approval_counts_updated', handleUpdate);
    socket.on('notification', handleUpdate);
    return () => {
      socket.off('approval_counts_updated', handleUpdate);
      socket.off('notification', handleUpdate);
    };
  }, [socket, isApproverUser, fetchApprovalCounts]);

  return (
    <div className="space-y-6">
      {/* ── Pending Approvals Quick Stats (CEO & GM Operations) ── */}
      {isApproverUser && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div onClick={() => navigate('/approvals?type=PROFILE&status=PENDING')} className="cursor-pointer">
            <StatCard
              title={t('profilesPending') || 'Profiles Pending'}
              value={approvalLoading ? '...' : (approvalCounts.pendingProfiles || 0)}
              icon={Contact}
              color="bg-blue-500"
            />
          </div>
          <div onClick={() => navigate('/approvals?type=PREQUALIFICATION&status=PENDING')} className="cursor-pointer">
            <StatCard
              title={t('prequalificationsPending') || 'Prequalifications Pending'}
              value={approvalLoading ? '...' : (approvalCounts.pendingPrequalifications || 0)}
              icon={ClipboardCheck}
              color="bg-amber-500"
            />
          </div>
          <div onClick={() => navigate('/approvals?type=TENDER&status=PENDING')} className="cursor-pointer">
            <StatCard
              title={t('tendersPending') || 'Tenders / Proposals Pending'}
              value={approvalLoading ? '...' : (approvalCounts.pendingTenders || 0)}
              icon={FileText}
              color="bg-purple-500"
            />
          </div>
        </div>
      )}

      {/* General Pipeline & Lead Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <StatCard
          title={t('pipelineValue')}
          value={formatCurrency(stats?.pipe || 0)}
          icon={TrendingUp}
          color="bg-blue-500"
        />
        <StatCard
          title={t('totalLeads')}
          value={stats?.total || 0}
          icon={Users}
          color="bg-purple-500"
        />
        <StatCard
          title={t('conversionRate')}
          value={`${stats?.conversion || 0}%`}
          icon={CheckCircle}
          color="bg-orange-500"
        />
      </div>
    </div>
  );
}
